import React, { useEffect, useState } from 'react';
import { useStowageStore } from 'src/context/StowageContext';
import { DefaultFlexStartWrapper, DefaultFlexStrechedWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellInputComponent, { InputChangeData } from 'src/components/common/odfjell-input-component/OdfjellInputComponent';
import { hasInvalidFields } from 'src/utils/aolFieldsHelper';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { ElementWrapper, HighlightPanel, PanelSectionContent, PanelSubHeader } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';
import OdfjellPanel from 'src/components/ui/odfjellPanel/OdfjellPanel';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import CargoNumber from '../../common/cargoNumber';
import { calculateVolume, calculateWeight } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { getCargoTemperature } from 'src/utils/stowage/stowageOperation/cargoTemperatureUtils';
import { roundTo3Decimals } from 'src/utils/numberHelper';
import Decimal from 'decimal.js';
import { splitCargoOperation } from 'src/utils/stowage/stowageOperation/splitUtils';
import { getCargoOperationWeight } from 'src/utils/stowage/cargoOperationUtils';
import { deepCopyStowagePlan } from 'src/factory/Stowage/StowagePlanFactory';
import { deepCopy } from 'src/utils/objectUtils';
import { STOWAGE_INPUT_DECIMAL_PLACES } from 'src/utils/stowage/stowagePrecision';

enum SplitType {
    FROM_CARGO_OPERATION,
    FROM_CARGO_IN_TRANSIT,
}

type SplitCargoOperationVM = {
    splitType: SplitType;
    initialNominatedWeight: number;
    initialNominatedVolume: Decimal;
    originalOperationNominatedWeight: number;
    originalOperationNominatedVolume: Decimal;
    maxSplitWeight: number;
    maxSplitVolume: Decimal;
    splitOperationNominatedWeight: number;
    splitOperationNominatedVolume: Decimal;
    splitOperationType: string;
    cargoTemperature: number;
    notValidFieldsArray?: [];
    hasInvalidFields?: boolean;
};

const SplitCargoOperationPanel: React.FC = () => {
    const [splitCargoOperationVM, setSplitCargoOperationVM] = useState<SplitCargoOperationVM | null>(null);
    const { activeStowagePlan, setIsSplitCargoOperationPanelOpen } = useStowageStore();
    const { selectedCargoOperation } = useStowageSelectionStore();
    const { saveStowageData } = usePostStowageApi();

    if (!selectedCargoOperation) return <></>;

    useEffect(() => {
        if (!activeStowagePlan || !selectedCargoOperation) {
            return;
        }

        const splitType = selectedCargoOperation.operationType === 'Transit' ? SplitType.FROM_CARGO_IN_TRANSIT : SplitType.FROM_CARGO_OPERATION;

        const cargoTemperature = getCargoTemperature(activeStowagePlan, selectedCargoOperation.cargo);
        const initialNominatedVolume = calculateVolume(
            new Decimal(selectedCargoOperation.nominatedWeight),
            selectedCargoOperation.cargo,
            cargoTemperature
        );
        const maxSplitWeight = splitType === SplitType.FROM_CARGO_IN_TRANSIT ? getMaxUnloadingCargoWeight() : selectedCargoOperation.nominatedWeight;
        const maxSplitVolume = calculateVolume(new Decimal(maxSplitWeight), selectedCargoOperation.cargo, cargoTemperature);

        setSplitCargoOperationVM({
            splitType: splitType,
            initialNominatedWeight: selectedCargoOperation.nominatedWeight,
            initialNominatedVolume: initialNominatedVolume,
            originalOperationNominatedWeight: selectedCargoOperation.nominatedWeight,
            originalOperationNominatedVolume: initialNominatedVolume,
            maxSplitWeight: maxSplitWeight,
            maxSplitVolume: maxSplitVolume,
            splitOperationNominatedWeight: 0,
            splitOperationNominatedVolume: new Decimal(0),
            splitOperationType: splitType === SplitType.FROM_CARGO_IN_TRANSIT ? 'Unloading' : selectedCargoOperation.operationType,
            cargoTemperature: cargoTemperature,
            notValidFieldsArray: [],
            hasInvalidFields: true,
        });
    }, []);

    const getMaxUnloadingCargoWeight = (): number => {
        const loadingWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Loading', 'nominated');
        const unloadingWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Unloading', 'nominated');
        return roundTo3Decimals(loadingWeight - unloadingWeight);
    };

    const onCancel = () => {
        setIsSplitCargoOperationPanelOpen(false);
    };

    const onUpdateSplitOperationNominatedVolume = (vm: SplitCargoOperationVM) => {
        vm.splitOperationNominatedWeight = calculateWeight(
            vm.splitOperationNominatedVolume,
            selectedCargoOperation.cargo,
            vm.cargoTemperature
        ).toNumber();
        vm.originalOperationNominatedWeight = vm.initialNominatedWeight - vm.splitOperationNominatedWeight;
        vm.originalOperationNominatedVolume = vm.initialNominatedVolume.minus(vm.splitOperationNominatedVolume);
    };

    const onUpdateSplitOperationNominatedWeight = (vm: SplitCargoOperationVM) => {
        vm.splitOperationNominatedVolume = calculateVolume(
            new Decimal(vm.splitOperationNominatedWeight),
            selectedCargoOperation.cargo,
            vm.cargoTemperature
        );
        vm.originalOperationNominatedVolume = vm.initialNominatedVolume.minus(vm.splitOperationNominatedVolume);
        vm.originalOperationNominatedWeight = vm.initialNominatedWeight - vm.splitOperationNominatedWeight;
    };

    const isFieldValid = (vm: SplitCargoOperationVM, field: string): boolean => {
        switch (field) {
            case 'splitOperationNominatedVolume':
                return (
                    vm.splitOperationNominatedVolume.gt(0) &&
                    (vm.splitType === SplitType.FROM_CARGO_IN_TRANSIT
                        ? vm.splitOperationNominatedVolume.lte(vm.maxSplitVolume)
                        : vm.splitOperationNominatedVolume.lt(vm.maxSplitVolume))
                );
            case 'splitOperationNominatedWeight':
                return (
                    vm.splitOperationNominatedWeight > 0 &&
                    (vm.splitType === SplitType.FROM_CARGO_IN_TRANSIT
                        ? vm.splitOperationNominatedWeight <= vm.maxSplitWeight
                        : vm.splitOperationNominatedWeight < vm.maxSplitWeight)
                );
        }
        return false;
    };

    const validateFields = (fields: string[]) => {
        fields.forEach((field) => {
            hasInvalidFields(splitCargoOperationVM, field, isFieldValid(splitCargoOperationVM, field));
        });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;

        if (name === 'splitOperationNominatedVolume') {
            splitCargoOperationVM.splitOperationNominatedVolume = new Decimal(+value);
            onUpdateSplitOperationNominatedVolume(splitCargoOperationVM);
        } else if (name === 'splitOperationNominatedWeight') {
            splitCargoOperationVM.splitOperationNominatedWeight = +value;
            onUpdateSplitOperationNominatedWeight(splitCargoOperationVM);
        }

        setSplitCargoOperationVM({ ...splitCargoOperationVM });
        validateFields(['splitOperationNominatedVolume', 'splitOperationNominatedWeight']);
        setSplitCargoOperationVM({ ...splitCargoOperationVM });
    };

    const onSave = async () => {
        if (canSave()) {
            const _stowagePlan = deepCopyStowagePlan(activeStowagePlan);
            const stowagePlanChanges = splitCargoOperation(
                _stowagePlan,
                deepCopy(selectedCargoOperation),
                splitCargoOperationVM.splitOperationNominatedWeight
            );
            if (stowagePlanChanges?.length > 0) {
                await saveStowageData(activeStowagePlan, stowagePlanChanges);
            }

            setIsSplitCargoOperationPanelOpen(false);
        }
    };

    const canSave = (): boolean => {
        return !splitCargoOperationVM.hasInvalidFields;
    };

    return (
        splitCargoOperationVM && (
            <OdfjellPanel
                headerTitle={splitCargoOperationVM.splitType === SplitType.FROM_CARGO_OPERATION ? 'Split cargo operation' : 'Add unloading operation'}
                defaultPosition="center"
                onCancel={onCancel}
                onAction={onSave}
                showCancelButton={true}
                isActionDisabled={!canSave()}
                preventOutsideClose={true}
                hasCurtain={true}
            >
                <PanelSectionContent>
                    <DefaultFlexStartWrapper>
                        <ElementWrapper>
                            <PanelSubHeader>
                                Call: {selectedCargoOperation.berthCall.portCall.portDisplayName} {'>'} {selectedCargoOperation.berthCall.berthName}
                            </PanelSubHeader>
                            <PanelSubHeader>
                                Cargo: <CargoNumber includeColoredBackground={true} cargo={selectedCargoOperation.cargo} />
                                {selectedCargoOperation.cargo.product.tradeName}
                            </PanelSubHeader>
                        </ElementWrapper>
                    </DefaultFlexStartWrapper>
                    <DefaultFlexStrechedWrapper>
                        <ElementWrapper>
                            <PanelSubHeader>
                                {splitCargoOperationVM.splitType === SplitType.FROM_CARGO_IN_TRANSIT ? 'Cargo in transit' : 'Initial operation'}
                            </PanelSubHeader>
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description={'Nominated weight'}
                                value={splitCargoOperationVM.originalOperationNominatedWeight.toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                name={'originalOperationNominatedWeight'}
                                dataObject={splitCargoOperationVM}
                                key={'input_original_cargo_operation_nominated_weight'}
                            />
                            <OdfjellInputComponent
                                isReadOnly={true}
                                type={'number'}
                                description={'Nominated volume'}
                                value={roundTo3Decimals(splitCargoOperationVM.originalOperationNominatedVolume.toNumber()).toFixed(
                                    STOWAGE_INPUT_DECIMAL_PLACES
                                )}
                                name={'originalOperationNominatedVolume'}
                                dataObject={splitCargoOperationVM}
                                key={'input_original_cargo_operation_nominated_volume'}
                            />
                        </ElementWrapper>
                        <HighlightPanel>
                            <ElementWrapper>
                                <PanelSubHeader>New operation</PanelSubHeader>
                                <OdfjellInputComponent
                                    type={'number'}
                                    value={splitCargoOperationVM.splitOperationNominatedWeight.toFixed(STOWAGE_INPUT_DECIMAL_PLACES)}
                                    name={'splitOperationNominatedWeight'}
                                    dataObject={splitCargoOperationVM}
                                    onChange={onInputChange}
                                    tabIndex={1}
                                    min={0}
                                    max={splitCargoOperationVM.maxSplitWeight}
                                    maxDecimals={STOWAGE_INPUT_DECIMAL_PLACES}
                                    unit={'MT'}
                                    key={'input_split_cargo_operation_nominated_weight'}
                                />
                                <OdfjellInputComponent
                                    type={'number'}
                                    value={roundTo3Decimals(splitCargoOperationVM.splitOperationNominatedVolume.toNumber()).toFixed(
                                        STOWAGE_INPUT_DECIMAL_PLACES
                                    )}
                                    name={'splitOperationNominatedVolume'}
                                    dataObject={splitCargoOperationVM}
                                    onChange={onInputChange}
                                    tabIndex={2}
                                    min={0}
                                    max={splitCargoOperationVM.maxSplitVolume.toNumber()}
                                    maxDecimals={STOWAGE_INPUT_DECIMAL_PLACES}
                                    unit={'m³'}
                                    key={'input_split_cargo_operation_nominated_volume'}
                                />
                            </ElementWrapper>
                        </HighlightPanel>
                    </DefaultFlexStrechedWrapper>
                </PanelSectionContent>
            </OdfjellPanel>
        )
    );
};

export default SplitCargoOperationPanel;
